'use client';

import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { usePathname } from 'next/navigation';
import { useMasquerade } from '@/hooks/useMasquerade';
import { clearAuthLocalStorage } from '@/lib/auth/logout';
import { mainContentFocusRef } from '@/lib/a11y/mainContentFocusRef';
import {
  END_MASQUERADE_LABEL,
  TESTING_CONSOLE_LABEL,
  getPrivilegeBadge,
  getPrivilegeLegend,
} from '@/lib/access-control/masquerade-ui';

/**
 * Once per sign-in (the server's `prompt_key` changes with every auth session), anyone holding
 * `roles.masquerade` is asked which role to act as, with their own role preselected. Mounted in AppShellProvider so it covers every sign-in method.
 *
 * Security: this component only DISPLAYS what GET /api/access-control/masquerade returns and
 * calls POST/DELETE on it. Who may masquerade as what is decided server-side (permission,
 * privilege ceiling, scope, audit row that fails closed); hiding a role here is not a control.
 * The `neutralaiz-role-prompted` cookie below is a UX throttle, not a security control: it is
 * readable and writable by the user, and forging it only skips this prompt.
 */

export const ROLE_PROMPT_COOKIE = 'neutralaiz-role-prompted';

const SKIP_PATH_PREFIXES = ['/login', '/auth', '/citizen', '/api'];
const CURRENT = 'current';

function readPromptFlag(): string | null {
  const entry = document.cookie.split('; ').find((c) => c.startsWith(`${ROLE_PROMPT_COOKIE}=`));
  if (!entry) return null;
  try {
    return decodeURIComponent(entry.slice(ROLE_PROMPT_COOKIE.length + 1));
  } catch {
    return null;
  }
}

function writePromptFlag(promptKey: string) {
  // Session cookie (no expiry); signout/route.ts expires it so the next sign-in is asked again.
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${ROLE_PROMPT_COOKIE}=${encodeURIComponent(promptKey)}; Path=/; SameSite=Lax${secure}`;
}

function PrivilegeBadge({ level }: { level: number }) {
  const badge = getPrivilegeBadge(level);
  return (
    <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium ${badge.color}`}>
      {badge.label}
    </span>
  );
}

/**
 * What the High / Medium / Low badge means. A real button (keyboard and screen reader, not
 * hover-only) that opens an inline panel; it never selects a role or submits.
 */
function PrivilegeLegend() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 text-sm">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="login-role-picker-legend"
        onClick={() => setOpen((value) => !value)}
        className="text-xs text-blue-300 underline underline-offset-2 hover:text-blue-200"
      >
        What do High, Medium and Low mean?
      </button>
      {open && (
        <div id="login-role-picker-legend" className="mt-2 rounded-md border border-slate-700 p-3 text-xs text-slate-300">
          <ul className="space-y-1">
            {getPrivilegeLegend().map((row) => (
              <li key={row.label}>
                {row.label} {row.range}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-slate-400">
            The badge shows how much access a role carries (its privilege level). You can only act as roles at or
            below your own level whose permissions you already hold; platform administrators can act as any role.
          </p>
        </div>
      )}
    </div>
  );
}

export function LoginRolePicker({ userId }: { userId: string | null }) {
  const pathname = usePathname() ?? '';
  const {
    canMasquerade,
    currentRole,
    availableRoles,
    activeRoleIds,
    staleMasquerade,
    promptKey,
    roleNames,
    activateMasquerade,
    deactivateMasquerade,
  } = useMasquerade();

  // Read on the client after mount so server and first client render agree.
  const [flag, setFlag] = useState<string | null | undefined>(undefined);
  const [selected, setSelected] = useState<string>(CURRENT);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFlag(readPromptFlag());
  }, [promptKey]);

  const onSkippedPath = SKIP_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  const visible =
    Boolean(userId) &&
    !onSkippedPath &&
    canMasquerade &&
    currentRole !== null &&
    promptKey !== null &&
    flag !== undefined &&
    flag !== promptKey;

  useEffect(() => {
    if (!visible) return;
    dialogRef.current?.querySelector<HTMLElement>('input[type="radio"]:checked')?.focus();
  }, [visible]);

  // Escape does not close this picker, so useModalA11y does not fit; but when Continue closes it, focus must not be
  // dropped on <body>: hand it to the main landmark (WCAG 2.4.3).
  useEffect(() => {
    if (!visible) return;
    return () => {
      const active = document.activeElement;
      if (!active || active === document.body) mainContentFocusRef.current?.focus();
    };
  }, [visible]);

  const onKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      // A choice is required; the default is one click.
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>('input:not([disabled]), button:not([disabled])') ?? [],
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  if (!visible || !userId || !currentRole || !promptKey) return null;

  const others = availableRoles.filter((role) => role.id !== currentRole.id);
  // A masquerade still in force in THIS session (its cookie is bound to this sign-in).
  const activeNames = activeRoleIds.length > 0
    ? roleNames.length > 0
      ? roleNames.join(', ')
      : availableRoles
          .filter((role) => activeRoleIds.includes(role.id))
          .map((role) => role.name)
          .join(', ') || `${activeRoleIds.length} role${activeRoleIds.length > 1 ? 's' : ''}`
    : null;

  async function onContinue() {
    if (!promptKey) return;
    setSubmitting(true);
    setError(null);
    try {
      // A masquerade left from an earlier sign-in is cleared first, through DELETE, so its end is
      // audited. Any failure to clear it stops here (fail closed): nothing is activated and the
      // picker stays open.
      if (staleMasquerade) await deactivateMasquerade({ throwOnError: true });
      if (selected === CURRENT) {
        // Own role: only a masquerade in force needs a call; otherwise nothing leaves the browser.
        // The call must be CONFIRMED: the picker never reports "your own role" while it is still on.
        if (activeRoleIds.length > 0) await deactivateMasquerade({ throwOnError: true });
      } else {
        await activateMasquerade([selected], { source: 'login' });
      }
      writePromptFlag(promptKey);
      setFlag(promptKey);
    } catch (err) {
      // Fail closed: stay open, flag not set, never activate client-side.
      setError(err instanceof Error && err.message ? err.message : 'Unable to change role');
    } finally {
      setSubmitting(false);
    }
  }

  const optionClass = (active: boolean) =>
    `flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-left ${
      active ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-500'
    }`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-role-picker-title"
        onKeyDown={onKeyDown}
        className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-6 text-slate-100 shadow-xl"
      >
        <h2 id="login-role-picker-title" className="text-lg font-semibold">
          Choose your role for this session
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          You can act as another role. You can change this later from the header: use {TESTING_CONSOLE_LABEL} to switch
          role, or {END_MASQUERADE_LABEL} in the banner.
        </p>
        <PrivilegeLegend />
        {activeNames && (
          <p className="mt-2 text-sm text-amber-300">You are currently acting as {activeNames}</p>
        )}
        {staleMasquerade && (
          <p className="mt-2 text-sm text-amber-300">
            A masquerade from an earlier sign-in was ended. Choose the role to act as for this session.
          </p>
        )}

        <div
          role="radiogroup"
          aria-labelledby="login-role-picker-title"
          className="mt-4 max-h-[50vh] space-y-2 overflow-y-auto"
        >
          <label className={optionClass(selected === CURRENT)}>
            <input
              type="radio"
              name="login-role"
              value={CURRENT}
              checked={selected === CURRENT}
              onChange={() => setSelected(CURRENT)}
              disabled={submitting}
              className="mt-1"
            />
            <span className="flex-1">
              <span className="block text-sm font-medium">
                Continue as {currentRole.name} (your role)
              </span>
            </span>
            <PrivilegeBadge level={currentRole.privilege_level} />
          </label>
          {others.map((role) => (
            <label key={role.id} className={optionClass(selected === role.id)}>
              <input
                type="radio"
                name="login-role"
                value={role.id}
                checked={selected === role.id}
                onChange={() => setSelected(role.id)}
                disabled={submitting}
                className="mt-1"
              />
              <span className="flex-1">
                <span className="block text-sm font-medium">{role.name}</span>
                {role.description && (
                  <span className="block text-xs text-slate-400">{role.description}</span>
                )}
              </span>
              <PrivilegeBadge level={role.privilege_level} />
            </label>
          ))}
        </div>

        {error && (
          <>
            <p role="alert" className="mt-3 text-sm text-red-400">
              {error}
            </p>
            {/* The picker covers the screen and cannot be dismissed, so when it cannot make progress
                (for example the server answers 503 "try again, or sign out") the way out is here. */}
            <form
              action="/auth/signout"
              method="post"
              onSubmit={() => clearAuthLocalStorage()}
              className="mt-2"
            >
              <button
                type="submit"
                className="text-sm text-slate-300 underline underline-offset-2 hover:text-white"
              >
                Sign out
              </button>
            </form>
          </>
        )}

        <button
          type="button"
          onClick={onContinue}
          disabled={submitting}
          className="mt-5 w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
        >
          Continue
        </button>
      </div>
    </div>
  );
}
